import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Upload, FileText, X, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { uploadProposalInvoice } from "@/lib/proposalsApi";
import { lookupUsersByRoles } from "@/lib/usersApi";

interface SalesUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

interface InvoiceUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposalId: string;
  onUploadSuccess: () => void;
  // When true, this proposal already has at least one invoice: uploading here just adds
  // another file (e.g. a split payment) instead of triggering the one-time booking-package
  // creation, so the finance-owner assignment isn't required again.
  hasExistingInvoice?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const InvoiceUploadModal: React.FC<InvoiceUploadModalProps> = ({
  open,
  onOpenChange,
  proposalId,
  onUploadSuccess,
  hasExistingInvoice = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedSalesUserId, setSelectedSalesUserId] = useState<string>("");
  const [salesUsers, setSalesUsers] = useState<SalesUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setSelectedFile(null);
      setSelectedSalesUserId("");
      if (!hasExistingInvoice) {
        loadFinanceUsers();
      }
    }
  }, [open, proposalId, hasExistingInvoice]);

  const loadFinanceUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await lookupUsersByRoles(["finance_staff", "ceo", "general_manager"]);
      const userList: SalesUser[] = (users || []).map((user) => {
        const roleName =
          user.role === "ceo"
            ? "CEO"
            : user.role === "general_manager"
            ? "General Manager"
            : "Finance";
        return {
          id: user.id,
          full_name: user.full_name,
          email: user.email,
          role: roleName,
        };
      });

      setSalesUsers(userList);
    } catch (error) {
      console.error("Error loading finance users:", error);
      toast({
        title: "Error loading users",
        description: "Could not load the list of finance team members.",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast({
        title: "Invalid file type",
        description: "Only PDF files are allowed.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile || !proposalId) return;
    if (!hasExistingInvoice && !selectedSalesUserId) return;

    try {
      setUploading(true);
      await uploadProposalInvoice(proposalId, selectedSalesUserId, selectedFile);

      toast(
        hasExistingInvoice
          ? {
              title: "Invoice added",
              description: "The additional invoice was attached to this proposal.",
            }
          : {
              title: "Invoice uploaded & Booking Package created",
              description: "The booking package is now ready for reservations.",
            }
      );

      setSelectedFile(null);
      setSelectedSalesUserId("");
      onOpenChange(false);
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setSelectedSalesUserId("");
    onOpenChange(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast({
          title: "Invalid file type",
          description: "Only PDF files are allowed.",
          variant: "destructive",
        });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB.",
          variant: "destructive",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const isConfirmDisabled = !selectedFile || uploading || (!hasExistingInvoice && !selectedSalesUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{hasExistingInvoice ? "Add Invoice" : "Upload Invoice & Create Booking"}</DialogTitle>
          <DialogDescription>
            {hasExistingInvoice
              ? "Add another payment invoice to this proposal (e.g. a split payment)."
              : "Upload the payment invoice and select the finance team member who will manage reservations."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              selectedFile
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25 hover:border-muted-foreground/50"
            }`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />

            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-primary" />
                <div className="text-left">
                  <p className="font-medium text-sm">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground">
                    PDF only (max 10MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          {!hasExistingInvoice && (
            <div className="space-y-2">
              <Label htmlFor="sales-select">
                Assign to Finance Team <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedSalesUserId}
                onValueChange={setSelectedSalesUserId}
                disabled={loadingUsers}
              >
                <SelectTrigger id="sales-select">
                  <SelectValue placeholder={loadingUsers ? "Loading..." : "Select a team member"} />
                </SelectTrigger>
                <SelectContent>
                  {salesUsers.map((salesUser) => (
                    <SelectItem key={salesUser.id} value={salesUser.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{salesUser.full_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {salesUser.email} • {salesUser.role}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {salesUsers.length === 0 && !loadingUsers && (
                    <div className="p-2 text-sm text-muted-foreground text-center">
                      No sales team members found
                    </div>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                This person will be responsible for managing the booking reference.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={isConfirmDisabled}
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {hasExistingInvoice ? "Add Invoice" : "Upload & Create Booking"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceUploadModal;
